import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import config from "react-global-configuration"
import IdleTimer from "react-idle-timer"

import { Card } from "components/Card/Card"
import { IdleModal } from "components/Modal/Modal"
import { cdn } from "core/cdn"
import withNetworkDetector from "hocs/withNetworkDetector"
import { dateDiff } from "lib/utils"
import { Call, CallState, Eta } from "types/payload"
import { CallStore } from "types/store"

import waitingRoomAnimation0 from "assets/animations/salle_attente_0prs.json"
import waitingRoomAnimation1 from "assets/animations/salle_attente_1prs.json"
import waitingRoomAnimation2 from "assets/animations/salle_attente_2prs.json"
import waitingRoomAnimation3 from "assets/animations/salle_attente_3prs.json"

import { Accepted } from "./Accepted"
import styles from "./Call.module.scss"
import Canceled from "./Canceled"
import Expired from "./Expired"
import { FifoWaiting } from "./FifoWaiting"
import { Hangup } from "./Hangup"
import { Waiting } from "./WaitingRoom"
import { useLanguage } from "locales"
import { canCustomerJoinRoom } from "./utils"

const getWaitingAnimation = (computed: number | null | undefined) => {
  if (computed == null) return waitingRoomAnimation0
  if (computed <= 5) return waitingRoomAnimation1
  if (computed <= 10) return waitingRoomAnimation2
  return waitingRoomAnimation3
}

const _IdleTimer = IdleTimer as any

const Render = (props: {
  call: Call
  loading: boolean
  createCall: () => void
  cancelCall: () => void
  logout: () => void
}) => {
  const { languages } = useLanguage()
  switch (props.call.state) {
    case CallState.EXPIRED:
      return <Expired createCall={props.createCall} logout={props.logout} />
    case CallState.ACCEPTED:
    case CallState.ACCEPTED_AND_SOON:
      const slot = new Date(props.call.start_date)
      const doctorName = `Dr ${props.call.doctor?.lastname}`
      return (
        <Accepted
          loading={props.loading}
          doctorName={doctorName}
          date={slot}
          callState={props.call.state}
          cancelCall={props.cancelCall}
        />
      )
    case CallState.FIFO_WAITING:
      return (
        <FifoWaiting
          loading={props.loading}
          call={props.call}
          eta={props.call.eta as Eta}
          cancelCall={props.cancelCall}
        />
      )
    case CallState.INITIALIZED:
      const now: Date = new Date()
      const expiredDate: Date = new Date(props.call.expires_at)
      const diff = dateDiff(now, expiredDate)
      return (
        <Waiting
          loading={props.loading}
          remainingMinutes={diff.min || 0}
          cancelCall={props.cancelCall}
        />
      )
    case CallState.HUNGUP:
      return <Hangup />
    case CallState.CANCELLED_BY_DOCTOR:
    case CallState.CANCELLED_BY_PATIENT:
      return (
        <Canceled
          createCall={props.createCall}
          logout={props.logout}
          cancelState={props.call.state}
        />
      )

    default:
      return <>{languages.errorCallState}</>
  }
}

const img = (state: CallState) => {
  switch (state) {
    case CallState.INITIALIZED:
    case CallState.ACCEPTED:
    case CallState.FIFO_WAITING:
      return cdn("images/attente_illustration.svg")
    case CallState.ACCEPTED_AND_SOON:
      return cdn("images/attente_bientot_illustration.svg")
    case CallState.EXPIRED:
    case CallState.CANCELLED_BY_DOCTOR:
      return cdn("images/pas_de_medecin_illustration.svg")
    case CallState.CANCELLED_BY_PATIENT:
      return cdn("images/patient_annulation_illustration.svg")
    case CallState.HUNGUP:
      return cdn("images/envoi_document_illustration.svg")
    default:
      return ""
  }
}


const LobbyCall: React.FC<
  CallStore & {
    createCall: () => void
  }
> = (props) => {
  const { languages } = useLanguage()
  const [callState, setCallState] = useState<CallState>(props.call.state)
  const [isIdle, setIdle] = useState(false)
  const timeoutMinutes = config.get("timeoutInactivity.minutes") || 15

  useEffect(() => {
    if (
      props.call.state === CallState.ACCEPTED &&
      dayjs(props.call.start_date).diff(dayjs(), "minute") <= 5
    )
      setCallState(CallState.ACCEPTED_AND_SOON)
    else if (
      [CallState.INITIALIZED].includes(
        props.call.state
      ) &&
      props.call.eta
    )
      setCallState(CallState.FIFO_WAITING)
    else setCallState(props.call.state)
  }, [props.call])

  const subtitle = (state: CallState, call: Call) => {
    const doctorName = `Dr ${call.doctor?.lastname}`

    switch (state) {
      case CallState.CANCELLED_BY_DOCTOR:
        return languages.noBill
      case CallState.ACCEPTED_AND_SOON:
        return `${doctorName} ${languages.subtitleCallAcceptedAndSoon}`
      case CallState.ACCEPTED:
        return `${doctorName} ${languages.consultationScheduledAt}`
      case CallState.INITIALIZED:
      default:
        return undefined
    }
  }
  const title = (state: CallState) => {
    switch (state) {
      case CallState.EXPIRED:
        return languages.doctorBusy
      case CallState.HUNGUP:
        return null
      case CallState.CANCELLED_BY_DOCTOR:
        return languages.doctorCancelCall
      case CallState.CANCELLED_BY_PATIENT:
        return languages.youHaveCancelledCall
      case CallState.INITIALIZED:
      default:
        return ""
    }
  }  
  const imageUrl = callState === CallState.FIFO_WAITING ? undefined : img(callState)
  const waitingRoomAnimation = (callState === CallState.FIFO_WAITING && !canCustomerJoinRoom(props.call?.eta?.computed))
    ? getWaitingAnimation(props.call?.eta?.computed)
    : undefined

  return (
    <div className={styles.LobbyCall} data-testid="LobbyCall">
      <Card
        title={title(callState)}
        titleSize={props.call.state === CallState.HUNGUP ? "small" : undefined}
        subtitle={subtitle(callState, props.call)}
        message={props.message}
        imgUrl={imageUrl}
        animation={waitingRoomAnimation}
        imgSize={
          [
            CallState.CANCELLED_BY_PATIENT,
            CallState.CANCELLED_BY_DOCTOR,
            CallState.EXPIRED,
          ].includes(callState)
            ? undefined
            : "large"
        }
      >
        <Render {...props} call={{ ...props.call, state: callState }} />
      </Card>

      {callState === CallState.EXPIRED ||
        callState === CallState.CANCELLED_BY_DOCTOR ||
        callState === CallState.CANCELLED_BY_PATIENT ? (
        /* eslint-disable-next-line react/jsx-pascal-case */
        <_IdleTimer
          timeout={1000 * 60 * timeoutMinutes}
          onIdle={() => {
            setIdle(true)
          }}
        />
      ) : null}
      <IdleModal
        visible={isIdle}
        onAccept={() => {
          setIdle(false)
        }}
        isIdle={isIdle}
      />
    </div>
  )
}

export default withNetworkDetector(LobbyCall) as any
